/**
 * EditBoardColumnModal Component
 * Modal for editing existing board columns
 * Handles column name and color updates
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Avatar, Divider, Tooltip } from '@mui/material';
import { Edit, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { COLORS, isValidHex } from '../../shared/colors';
import { BOARD_COLUMN_PRESET_COLORS as PRESET_COLORS } from '../../shared/appConstants';
import CustomColorPopover from '../shared/CustomColorPopover';

const EditBoardColumnModal = ({ open, onClose, onSubmit, columnData }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [colour, setColour] = useState(PRESET_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

  useEffect(() => {
    if (columnData) {
      setName(columnData.title || '');
      setColour(columnData.colour || PRESET_COLORS[0]);
    }
    if (open) {
      setIsSubmitting(false);
    }
  }, [columnData, open]);

  const previewColor = isValidHex(colour) ? colour : PRESET_COLORS[0];

  const handleCancel = () => {
    if (isSubmitting) return;
    setName(columnData?.title || '');
    setColour(columnData?.colour || PRESET_COLORS[0]);
    onClose && onClose();
  };

  const hasChanged = name.trim() !== columnData?.title || colour !== (columnData?.colour || PRESET_COLORS[0]);

  const handleUpdate = async () => {
    if (!name.trim() || !hasChanged || isSubmitting || !isValidHex(colour)) return;

    setIsSubmitting(true);
    try {
      await onSubmit?.({ ...columnData, name: name.trim(), colour });
      onClose && onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: COLORS.brand.primaryDark }}>
            <Edit />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">{t('boardColumn.editColumn')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('boardColumn.updateColumnName')}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label={t('boardColumn.columnName')}
          value={name}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 25) {
              setName(value);
            }
          }}
          disabled={isSubmitting}
          placeholder={t('boardColumn.enterColumnName')}
          required
          variant="outlined"
          inputProps={{ maxLength: 25 }}
          helperText={`${name.length}/25 characters`}
          sx={{ mb: 3 }}
        />

        {/* Color Picker */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
            Column Color
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            {PRESET_COLORS.map((c) => (
              <Tooltip key={c} title={c}>
                <Box
                  onClick={() => setColour(c)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: colour === c ? `2px solid ${COLORS.gray[800]}` : '2px solid transparent',
                    transition: 'transform 0.15s',
                    '&:hover': { transform: 'scale(1.15)' }
                  }}
                >
                  {colour === c && <Check sx={{ fontSize: 14, color: '#fff' }} />}
                </Box>
              </Tooltip>
            ))}
            {/* Custom color picker */}
            <Tooltip title="Custom color">
              <Box
                onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: !PRESET_COLORS.includes(colour) && isValidHex(colour) ? `2px solid ${COLORS.gray[800]}` : `2px dashed ${COLORS.gray[400]}`,
                  background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  transition: 'transform 0.15s',
                  overflow: 'hidden',
                  '&:hover': { transform: 'scale(1.15)' }
                }}
              >
                {!PRESET_COLORS.includes(colour) && isValidHex(colour) && <Check sx={{ fontSize: 14, color: '#fff' }} />}
              </Box>
            </Tooltip>

            <CustomColorPopover
              open={Boolean(colorPickerAnchor)}
              anchorEl={colorPickerAnchor}
              onClose={() => setColorPickerAnchor(null)}
              colour={colour}
              onChange={setColour}
            />
          </Box>
          {/* Preview */}
          <Box
            sx={{
              mt: 2.5,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: previewColor + '29',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: previewColor }} />
            <Typography variant="caption" fontWeight={500} color="text.secondary">
              {name || 'Column preview'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancel} variant="outlined" disabled={isSubmitting}>{t('common.cancel')}</Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={isSubmitting || !name.trim() || !hasChanged || !isValidHex(colour)}
          sx={{
            bgcolor: COLORS.brand.primaryDark,
            color: COLORS.white,
            '&:hover': { bgcolor: COLORS.brand.primaryDarkHover, color: COLORS.white },
            '&.Mui-disabled': {
              bgcolor: COLORS.slate[200],
              color: COLORS.slate[400]
            }
          }}
        >
          {isSubmitting ? t('common.updating', 'Updating...') : t('common.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBoardColumnModal;
